<template>
    <div>
        <!-- SR-only helper/description for the whole group -->
        <p id="loclang-help" class="sr-only">Choose a location first, then pick a language for accurate search volumes.</p>

        <!-- Live region to announce selections to screen readers -->
        <div class="sr-only" aria-live="polite">{{ liveStatus }}</div>

        <v-row aria-describedby="loclang-help">
            <!-- LOCATION -->
            <v-col cols="12" sm="6" class="pt-0">
                <v-autocomplete
                    id="location_select"
                    v-model="selectedLocation"
                    :items="locations"
                    item-text="location_name"
                    item-value="id"
                    prepend-inner-icon="mdi-map-marker"
                    rounded
                    filled
                    hide-details
                    :label="'Location'"
                    placeholder="Start typing to search"
                    color="primary"
                    return-object
                    clearable
                    clear-icon="mdi-close"
                    :no-filter="countriesOnly"
                    :search-input.sync="search"
                    :loading="isLoadingLocations"
                    :aria-describedby="'loclang-help'"
                    aria-required="true"
                    spellcheck="false"
                    autocapitalize="off"
                    autocomplete="off"
                    @focus="onLocationFocus"
                    @change="onLocationSelected">
                    <template v-slot:selection="{ item }">
                        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: rgba(0, 0, 0, 0.87)">
                            {{ item.location_name }}
                            <span v-if="item.parent_name" class="location-separator" style="text-transform: uppercase">{{ item.parent_iso_code }}</span>
                        </span>
                    </template>

                    <!-- Dropdown-Items -->
                    <template v-slot:item="{ item }">
                        {{ item.location_name }}
                        <span v-if="item.parent_name" class="location-separator">{{ item.location_code_parent }}</span>
                        <span v-if="item.parent_name" class="location-separator" style="text-transform: uppercase">{{ item.parent_iso_code }}</span>
                    </template>
                </v-autocomplete>
            </v-col>

            <!-- LANGUAGE -->
            <v-col cols="12" sm="6" class="pt-0">
                <v-select
                    id="language_select"
                    v-model="selectedLanguage"
                    :items="languages"
                    item-text="language_name"
                    item-value="id"
                    prepend-inner-icon="mdi-translate"
                    rounded
                    :label="'Language'"
                    no-data-text="-"
                    :placeholder="languages.length === 0 ? '-' : ''"
                    hide-details
                    clear-icon="mdi-close"
                    filled
                    color="primary"
                    :loading="isLoadingLanguages"
                    :disabled="!selectedLocation || (selectedLocation && selectedLocation.location_name === 'International')"
                    :aria-describedby="'loclang-help'"
                    :aria-disabled="!selectedLocation || (selectedLocation && selectedLocation.location_name === 'International') ? 'true' : 'false'"
                    aria-required="true"
                    return-object
                    clearable
                    @change="onLanguageSelected" />
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import locs from "./helper/locs"

    export default {
        name: "LocationSelection",
        data() {
            return {
                selectedLocation: null,
                selectedLanguage: null,
                locations: [],
                languages: [],
                search: "",
                liveStatus: "", // SR live announcements
                isLoadingLocations: false, // expose loading to AT
                isLoadingLanguages: false,
                allCountriesLoaded: false
            }
        },
        props: {
            suggest_tab: String,
            countriesOnly: {
                type: Boolean,
                default: false
            }
        },
        methods: {
            async fetchLocations(query = false) {
                try {
                    this.isLoadingLocations = true
                    const post_data = {}
                    if (query) post_data.query = query

                    const url = process.env.VUE_APP_APIURL + "/service/google-locations"
                    let response_data = await this.$helpers.axiosPost(url, post_data)

                    let fetchedLocations
                    if (this.suggest_tab !== "tab-2") {
                        fetchedLocations = response_data.data.locations
                    } else {
                        fetchedLocations = response_data.data.locations.filter((x) => locs.stateItems.findIndex((y) => y.value == x.location_code) > -1)
                    }
                    if (this.countriesOnly) {
                        fetchedLocations = fetchedLocations.filter((x) => x.location_type == "Country")
                    }
                    this.locations = fetchedLocations
                } catch (error) {
                    console.error("Error fetching locations:", error)
                } finally {
                    this.isLoadingLocations = false
                }
            },

            async fetchLanguagesInit(location) {
                try {
                    this.isLoadingLanguages = true
                    const post_data = { location }
                    const url = process.env.VUE_APP_APIURL + "/service/google-languages"
                    let response_data = await this.$helpers.axiosPost(url, post_data)

                    if (response_data.data.languages.length > 0) {
                        this.languages = response_data.data.languages
                        this.selectedLanguage = this.languages[0]
                        this.selectedLocation.language = this.selectedLanguage
                        this.liveStatus = `Language set to ${this.selectedLanguage.language_name} for ${this.selectedLocation.location_name}.`
                    } else {
                        this.languages = []
                        this.selectedLocation.language = null
                        this.liveStatus = `No languages available for ${this.selectedLocation.location_name}.`
                    }
                    this.buildLangLocObject()
                } catch (error) {
                    console.error("Error fetching locations:", error)
                } finally {
                    this.isLoadingLanguages = false
                }
            },

            onLocationFocus() {
                if (this.countriesOnly && !this.allCountriesLoaded && !this.isLoadingLocations) {
                    // Alle Länder einmalig laden, damit nach einer Auswahl direkt weitere Länder sichtbar sind
                    this.fetchLocations().then(() => {
                        this.allCountriesLoaded = true
                    })
                }
            },

            onLocationSelected() {
                if (this.selectedLocation) {
                    this.liveStatus = `Location selected: ${this.selectedLocation.location_name}.`
                    if (this.selectedLocation.location_name !== "International") {
                        this.fetchLanguagesInit(this.selectedLocation)
                    } else {
                        this.languages = []
                        this.buildLangLocObject()
                    }
                } else {
                    this.fetchLocations()
                }
            },

            onLanguageSelected() {
                if (this.selectedLanguage) {
                    this.selectedLocation.language = this.selectedLanguage
                    this.liveStatus = `Language selected: ${this.selectedLanguage.language_name}.`
                    this.buildLangLocObject()
                }
            },

            buildLangLocObject() {
                if (this.selectedLocation) {
                    let questions = !this.selectedLocation.language
                        ? {}
                        : !locs.stateItems[locs.stateItems.findIndex((x) => x.value == this.selectedLocation.location_code && x.ads_lang_code == this.selectedLocation.language.language_code)]
                        ? {}
                        : locs.stateItems[locs.stateItems.findIndex((x) => x.value == this.selectedLocation.location_code && x.ads_lang_code == this.selectedLocation.language.language_code)]

                    let langloc = {
                        loc: this.selectedLocation.location_name,
                        value: this.selectedLocation.location_code,
                        ads_lang_code: this.selectedLocation.language == null ? null : this.selectedLocation.language.language_code,
                        cc:
                            this.selectedLocation.location_type == "Country"
                                ? this.selectedLocation.country_iso_code
                                    ? this.selectedLocation.country_iso_code.toLowerCase()
                                    : null
                                : this.selectedLocation.parent_iso_code,
                        serp_lang_code:
                            this.selectedLocation.location_type == "Country" && this.selectedLocation.language
                                ? this.selectedLocation.language.language_code
                                : this.selectedLocation.parent_iso_code
                                ? this.selectedLocation.parent_iso_code
                                : null,
                        questions: questions.questions,
                        questionParams: questions.questionParams,
                        prepositionParams: questions.prepositionParams,
                        comparisonParams: questions.comparisonParams
                    }

                    this.$store.dispatch("SET_SELECTED_LOCATION", {
                        selectedLocation: this.selectedLocation,
                        locations: this.locations,
                        selectedLanguage: this.selectedLanguage,
                        languages: this.languages
                    })
                    this.$emit("setLangLoc", langloc)
                    localStorage.setItem("location_selection", JSON.stringify({ selectedLocation: this.selectedLocation, selectedLanguage: this.selectedLanguage }))
                }
            },

            setLangloc() {
                // (kept from your original, not used)
            }
        },

        watch: {
            async suggest_tab(val) {
                if (val == "tab-2") {
                    if (locs.stateItems.findIndex((x) => x.value == this.selectedLocation.location_code) == -1) {
                        await this.fetchLocations("Germany")
                        this.selectedLocation = this.locations[0]
                        this.fetchLanguagesInit(this.selectedLocation)
                    } else {
                        this.locations = this.locations.filter((x) => locs.stateItems.findIndex((y) => y.value == x.location_code) > -1)
                    }
                } else {
                    this.fetchLocations(this.search)
                }
            },

            search(val) {
                if (!val) return
                this.fetchLocations(this.search)
            },

            "$store.state.selected_location"() {
                this.locations = this.$store.state.selected_location.locations
                this.selectedLocation = this.$store.state.selected_location.selectedLocation
                this.languages = this.$store.state.selected_location.languages
                this.selectedLanguage = this.$store.state.selected_location.selectedLanguage
            }
        },

        async mounted() {
            if (!localStorage.location_selection) {
                await this.fetchLocations("Germany")
                this.selectedLocation = this.locations[0]
                this.fetchLanguagesInit(this.selectedLocation)
            } else {
                let loc = JSON.parse(localStorage.location_selection)
                if (this.countriesOnly) {
                    if (!loc.selectedLocation || loc.selectedLocation.location_type !== "Country") {
                        await this.fetchLocations("Germany")
                        this.selectedLocation = this.locations[0]
                        this.fetchLanguagesInit(this.selectedLocation)
                    } else {
                        await this.fetchLocations(loc.selectedLocation.location_name)
                        this.selectedLocation = loc.selectedLocation
                        this.fetchLanguagesInit(this.selectedLocation)
                        this.selectedLanguage = loc.selectedLanguage
                        this.buildLangLocObject()
                    }
                } else if (this.suggest_tab == "tab-2") {
                    if (locs.stateItems.findIndex((x) => x.value == loc.selectedLocation.location_code) == -1) {
                        await this.fetchLocations("Germany")
                        this.selectedLocation = this.locations[0]
                        this.fetchLanguagesInit(this.selectedLocation)
                    } else {
                        await this.fetchLocations(loc.selectedLocation.location_name)
                        this.selectedLocation = loc.selectedLocation
                        this.fetchLanguagesInit(this.selectedLocation)
                        this.selectedLanguage = loc.selectedLanguage
                        this.locations = this.locations.filter((x) => locs.stateItems.findIndex((y) => y.value == x.location_code) > -1)
                    }
                } else {
                    await this.fetchLocations(loc.selectedLocation.location_name)
                    this.selectedLocation = loc.selectedLocation
                    this.fetchLanguagesInit(this.selectedLocation)
                    this.selectedLanguage = loc.selectedLanguage
                }
                this.buildLangLocObject()
            }
        }
    }
</script>

<style scoped>
    .location-separator::before {
        content: ", ";
    }

    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
</style>
